// src/ReportCard.js

import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { CheckBadgeIcon, UserCircleIcon } from "@heroicons/react/24/solid"; // Import from Heroicons v2

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ReportCard = () => {
  const { id } = useParams(); // Get the ID from the URL parameters

  const [mockTest, setMockTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMockTestDataById = async () => {
      try {
        const response = await fetch(`/api/fetch-mocktestdata/${id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();
        setMockTest(data);
      } catch (err) {
        setError("Error fetching mock test data by id");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchMockTestDataById();
  }, [id]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;

  const Speaking = mockTest?.sectionScores.speaking || 0;
  const Writing = mockTest?.sectionScores.writing || 0;
  const Reading = mockTest?.sectionScores.reading || 0;
  const Listening = mockTest?.sectionScores.listening || 0;

  const TestDate = new Date(mockTest?.updatedAt).toLocaleDateString();
  const Username = mockTest?.username;

  const maxScore = 90; // Maximum score possible

  const reportData = {
    studentName: Username,
    studentId: "123456",
    testDate: TestDate,
    testCenter: "PrepNexa Online Test",
    scores: [
      {
        section: "Speaking",
        value: Speaking, // Use raw score
        color: "rgba(92, 184, 92, 0.6)",
        borderColor: "rgba(92, 184, 92, 1)",
      },
      {
        section: "Writing",
        value: Writing, // Use raw score
        color: "rgba(91, 192, 222, 0.6)",
        borderColor: "rgba(91, 192, 222, 1)",
      },
      {
        section: "Reading",
        value: Reading, // Use raw score
        color: "rgba(240, 173, 78, 0.6)",
        borderColor: "rgba(240, 173, 78, 1)",
      },
      {
        section: "Listening",
        value: Listening, // Use raw score
        color: "rgba(155, 89, 182, 0.6)",
        borderColor: "rgba(155, 89, 182, 1)",
      },
    ],
    remarks: "Excellent performance overall. Keep up the good work!",
  };

  // Calculate overall score out of 90
  const overallScore =
    reportData.scores.reduce((total, score) => total + score.value, 0) /
    reportData.scores.length;

  const barChartData = {
    labels: reportData.scores.map((score) => score.section),
    datasets: [
      {
        label: "Scores",
        data: reportData.scores.map((score) => score.value),
        backgroundColor: reportData.scores.map((score) => score.color),
        borderColor: reportData.scores.map((score) => score.borderColor),
        borderWidth: 1,
      },
    ],
  };

  const pieChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: (context) => `${context.chart.data.labels[0]} Score`,
      },
    },
  };

  const pieCharts = reportData.scores.map((score, index) => {
    const pieChartData = {
      labels: ["Score", "Remaining"],
      datasets: [
        {
          label: `${score.section} Score`,
          data: [score.value, maxScore - score.value],
          backgroundColor: [score.color, "rgba(192, 192, 192, 0.6)"],
          borderColor: [score.borderColor, "rgba(192, 192, 192, 1)"],
          borderWidth: 1,
        },
      ],
    };
    return (
      <div key={index} className="w-full md:w-1/2 lg:w-1/4 p-2">
        <Pie data={pieChartData} options={pieChartOptions} />
      </div>
    );
  });

  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "PTE Scores (Bar Chart)",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: maxScore, // Set max value to 90
        ticks: {
          callback: function (value) {
            return value; // Display raw score on Y-axis
          },
        },
      },
    },
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow-md rounded-lg relative">
      <img
        src="/image/6.png"
        alt="Institute Logo"
        className="absolute top-7 right-4 h-auto"
        width="140"
        height="150"
      />
      <h1 className="text-2xl font-bold mb-4 text-center">PTE Report Card</h1>
      <div className="flex flex-row items-start justify-between mt-10 mb-6 border-t-2 border-gray-300 pt-8">
        <div className="flex items-center">
          <UserCircleIcon className="h-24 w-24 text-gray-400 mr-4" />
          <div>
            <h2 className="text-xl font-semibold">Student Details</h2>
            <p>
              <strong>Name:</strong> {reportData.studentName}
            </p>
            <p>
              <strong>ID:</strong> {reportData.studentId}
            </p>
          </div>
        </div>
        <div>
          <h2 className="text-xl font-semibold">Test Details</h2>
          <p>
            <strong>Date:</strong> {reportData.testDate}
          </p>
          <p>
            <strong>Center:</strong> {reportData.testCenter}
          </p>
        </div>
        <div className="flex items-center bg-blue-100 text-blue-800 p-4 flex-col justify-center">
          <div className="bg-blue-200 px-2 py-1 rounded-t-lg w-full text-center font-semibold">
            Overall Score
          </div>
          <div className="flex items-center justify-center mt-2">
            <CheckBadgeIcon className="h-6 w-6 mr-1" />
            <span className="text-2xl font-bold">
              {overallScore.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
      <div className="mt-6">
        <h2 className="text-xl font-semibold border-b-2 mt-10 border-gray-300 pb-2">
          Communicative Skills
        </h2>
      </div>
      <div className="py-10 flex flex-row items-center flex-wrap">
        {pieCharts}
      </div>
      <div className="mt-6">
        <h2 className="text-xl font-semibold border-b-2 border-gray-300 pb-2">
          Scores
        </h2>
        <Bar data={barChartData} options={barOptions} />
      </div>
      <div className="mt-6">
        <h2 className="text-xl font-semibold border-b-2 border-gray-300 pb-2">
          Remarks
        </h2>
        <p>{reportData.remarks}</p>
      </div>
    </div>
  );
};

export default ReportCard;
